import { Box, IconButton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { optionLabel } from "../../helpers/optionLabels";
import { useState } from "react";
import Button from "components/basic/button";
import InlineEditRow from "components/basic/inlineEditRow";
import { formatPhoneNumber } from "pages/formTools";
import InlineFileEditRow from "components/basic/inlineFileEditRow";
import { headerOffset } from "components/basic/header";
import Typography from "components/basic/typography";
import EditIcon from "resources/images/EditIcon.png";
import GooglePlacesSearch, {
  PlaceResult,
} from "components/basic/googlePlacesSearch";
import { useNavigate } from "react-router-dom";
import {
  ChargerEstimatorData,
  ChargerEstimatorToolsPagesProps,
} from "pages/chargerEstimator";
import ScrollContainer from "components/basic/scrollContainer";

// Address row matching InlineEditRow's look, but powered by Google Places so
// editing also refreshes the latitude/longitude/placeId.
const AddressEditRow: React.FC<{
  value: string;
  onSave: (place: { address: string } & Partial<PlaceResult>) => void;
}> = ({ value, onSave }) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const [editing, setEditing] = useState(false);
  const [draftAddress, setDraftAddress] = useState(value);
  const [draftPlace, setDraftPlace] = useState<PlaceResult | null>(null);

  const startEditing = () => {
    setDraftAddress(value);
    setDraftPlace(null);
    setEditing(true);
  };

  const handleSave = () => {
    // Only attach coordinates when the text still matches the picked place.
    if (draftPlace && draftPlace.address === draftAddress) {
      onSave({
        address: draftAddress,
        latitude: draftPlace.latitude,
        longitude: draftPlace.longitude,
        placeId: draftPlace.placeId,
      });
    } else {
      onSave({
        address: draftAddress,
        latitude: undefined,
        longitude: undefined,
        placeId: undefined,
      });
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraftAddress(value);
    setDraftPlace(null);
    setEditing(false);
  };

  return (
    <div
      style={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: `${theme.spacing(0.75)} 0`,
      }}
    >
      {!editing ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: theme.spacing(1),
          }}
        >
          <Typography variant="body2" style={{ opacity: 0.7 }}>
            {t("review.address")}
          </Typography>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(0.5),
            }}
          >
            <Typography variant="body2" style={{ fontWeight: "bold" }}>
              {value || "—"}
            </Typography>

            <IconButton size="small" onClick={startEditing}>
              <img
                src={EditIcon}
                alt={t("review.edit")}
                width={15}
                height={15}
              />
            </IconButton>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.5),
          }}
        >
          <Typography variant="body2" style={{ opacity: 0.7 }}>
            {t("review.address")}
          </Typography>

          <GooglePlacesSearch
            name="address"
            size="tiny"
            value={draftAddress}
            placeHolder="1234 Enter Street, City, Province, A1B 2C3"
            onInputChange={(text) => {
              setDraftPlace(null);
              setDraftAddress(text);
            }}
            onSelect={(place) => {
              setDraftPlace(place);
              setDraftAddress(place.address);
            }}
          />

          <div style={{ display: "flex", gap: theme.spacing(1) }}>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={handleSave}
              style={{ color: "white", padding: `0 ${theme.spacing(2)}` }}
            >
              {t("review.save")}
            </Button>

            <Button size="small" variant="text" onClick={handleCancel}>
              {t("review.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();

  return (
    <Typography
      variant="h5"
      style={{
        fontWeight: "bold",
        marginTop: theme.spacing(2.5),
        marginBottom: theme.spacing(0.5),
        borderBottom: `2px solid ${theme.palette.warning.main}`,
        paddingBottom: theme.spacing(0.5),
      }}
    >
      {title}
    </Typography>
  );
};

const toYesNo = (value: boolean | undefined): "Yes" | "No" =>
  value ? "Yes" : "No";

const toInstallationType = (value: string) => {
  if (value === "homeowner") return "Homeowner";
  if (value === "business") return "Business or Fleet Manager";
  return value;
};

const ReviewDetails: React.FC<ChargerEstimatorToolsPagesProps> = ({
  chargerEstimatorData,
  setChargerEstimatorData,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("estimator");
  const formatAnswer = (value: string) => optionLabel(t, value);
  const contentMaxWidth = 560;
  const scrollContainerMaxWidth = 620;

  const setStringField =
    (field: keyof ChargerEstimatorData) => (value: string) => {
      setChargerEstimatorData((prev) => ({ ...prev, [field]: value }));
    };

  const setBooleanField =
    (field: keyof ChargerEstimatorData) => (value: string) => {
      setChargerEstimatorData((prev) => ({
        ...prev,
        [field]: value === "Yes",
      }));
    };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        height: `calc(100dvh - ${headerOffset(1)})`,
        paddingX: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "640px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Typography
          variant="h4"
          align="center"
          style={{
            fontWeight: "bold",
            paddingTop: theme.spacing(3),
          }}
          sx={{ fontSize: "clamp(1.5rem, 5vw, 2.5rem)" }}
        >
          {t("review.title")}
        </Typography>

        <ScrollContainer>
          <div
            style={{
              flex: 1,
              width: "100%",
              maxWidth: `${scrollContainerMaxWidth}px`,
              alignSelf: "center",
              minHeight: 0,
              paddingRight: theme.spacing(2),
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: `${contentMaxWidth}px`,
                margin: "0 auto",
              }}
            >
              <SectionHeader title={t("review.property")} />
              <AddressEditRow
                value={chargerEstimatorData.address}
                onSave={(place) =>
                  setChargerEstimatorData((prev) => ({
                    ...prev,
                    address: place.address,
                    latitude: place.latitude,
                    longitude: place.longitude,
                    placeId: place.placeId,
                  }))
                }
              />
              <InlineEditRow
                label={t("review.installation_type")}
                value={toInstallationType(
                  chargerEstimatorData.installationType,
                )}
                onSave={(value) =>
                  setStringField("installationType")(
                    value === "Homeowner" ? "homeowner" : "business",
                  )
                }
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["Homeowner", "Business or Fleet Manager"]}
              />

              <SectionHeader title={t("review.vehicle_home")} />
              <InlineEditRow
                label={t("review.vehicle_type")}
                value={chargerEstimatorData.vehicleType}
                onSave={setStringField("vehicleType")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["Tesla", "Other"]}
              />
              <InlineEditRow
                label={t("review.house_type")}
                value={chargerEstimatorData.houseType}
                onSave={setStringField("houseType")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["Detached", "Semi-Detached", "Townhouse"]}
              />
              <InlineEditRow
                label={t("review.charger_location")}
                value={chargerEstimatorData.chargerLocation}
                onSave={setStringField("chargerLocation")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["Outdoor", "Indoor"]}
              />
              <InlineEditRow
                label={t("review.charger_setup")}
                value={chargerEstimatorData.chargerSetup}
                onSave={setStringField("chargerSetup")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={[
                  "NEMA 14-50 Outlet Only",
                  "Tesla Wall Connector with NEMA 14-50",
                  "NEMA 14-50 With Level 2 Charger",
                  "Tesla Wall hardwired",
                  "Hardwired with Level 2 Charger",
                ]}
              />

              <SectionHeader title={t("review.power")} />
              <InlineEditRow
                label={t("review.panel_location")}
                value={chargerEstimatorData.electricalPanelLocation}
                onSave={setStringField("electricalPanelLocation")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["Main Floor", "Basement", "Garage"]}
              />
              <InlineEditRow
                label={t("review.service_size")}
                value={chargerEstimatorData.mainServiceSize}
                onSave={setStringField("mainServiceSize")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["100A", "200A", "400A"]}
              />
              <InlineEditRow
                label={t("review.wire_length")}
                value={chargerEstimatorData.wireLength}
                onSave={setStringField("wireLength")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["10 FT", "20 FT", "30 FT", "40 FT", "50 FT"]}
              />
              <InlineFileEditRow
                label={t("review.panel_photo")}
                file={chargerEstimatorData.electricalPanelPhoto}
                url={chargerEstimatorData.electricalPanelPhotoURL}
                uploadLabel="Upload Electrical Panel Photo"
                onSave={(file) =>
                  setChargerEstimatorData((prev) => ({
                    ...prev,
                    electricalPanelPhoto: file,
                  }))
                }
              />
              <InlineFileEditRow
                label={t("review.breaker_photo")}
                file={chargerEstimatorData.mainBreakerPhoto}
                url={chargerEstimatorData.mainBreakerPhotoURL}
                uploadLabel="Upload Main Breaker Photo"
                onSave={(file) =>
                  setChargerEstimatorData((prev) => ({
                    ...prev,
                    mainBreakerPhoto: file,
                  }))
                }
              />

              <SectionHeader title={t("review.preferences")} />
              <InlineEditRow
                label={t("review.purchase_type")}
                value={chargerEstimatorData.purchaseType}
                onSave={setStringField("purchaseType")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={[
                  "purchase charger",
                  "lease-to-own charger",
                  "provide own charger",
                ]}
              />
              <InlineEditRow
                label={t("review.elevator")}
                value={toYesNo(chargerEstimatorData.elevator)}
                onSave={setBooleanField("elevator")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["Yes", "No"]}
              />
              <InlineEditRow
                label={t("review.pool")}
                value={toYesNo(chargerEstimatorData.pool)}
                onSave={setBooleanField("pool")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["Yes", "No"]}
              />
              <InlineEditRow
                label={t("review.hot_tub")}
                value={toYesNo(chargerEstimatorData.hotTub)}
                onSave={setBooleanField("hotTub")}
                inputType="dropdown"
                formatOption={formatAnswer}
                options={["Yes", "No"]}
              />

              <SectionHeader title={t("review.contact")} />
              <InlineEditRow
                label={t("review.full_name")}
                value={chargerEstimatorData.fullName}
                onSave={setStringField("fullName")}
              />
              <InlineEditRow
                label={t("review.email")}
                value={chargerEstimatorData.email}
                onSave={setStringField("email")}
              />
              <InlineEditRow
                label={t("review.phone")}
                value={chargerEstimatorData.phoneNumber}
                onSave={setStringField("phoneNumber")}
                mask={formatPhoneNumber}
              />
              <InlineEditRow
                label={t("review.additional")}
                value={chargerEstimatorData.additionalInformation || ""}
                onSave={setStringField("additionalInformation")}
              />
            </div>
          </div>
        </ScrollContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: theme.spacing(2),
            paddingTop: theme.spacing(3),
            paddingBottom: { xs: theme.spacing(3), md: theme.spacing(4) },
            "& > *": { flex: { xs: "1 1 0", sm: "0 0 auto" } },
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            style={{
              color: theme.palette.common.white,
              fontWeight: "bold",
              padding: `0 ${theme.spacing(3.5)}`,
            }}
            onClick={() => navigate("?currentPage=contactInformation")}
          >
            {t("review.back")}
          </Button>

          <Button
            variant="contained"
            color="warning"
            size="large"
            style={{
              color: theme.palette.common.white,
              fontWeight: "bold",
              padding: `0 ${theme.spacing(3.5)}`,
            }}
            onClick={() => navigate("?currentPage=selectEstimate")}
          >
            {t("review.receive")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ReviewDetails;
